import { AnimatePresence, motion } from 'framer-motion';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { Camera, Image, Keyboard, Zap, ZapOff } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';

interface CameraDevice {
    id: string;
    label: string;
}

interface QrScannerEnhancedProps {
    onScanSuccess: (decodedText: string) => void;
    onScanError?: (error: string) => void;
    width?: number | string;
    height?: number | string;
    fps?: number;
    showBorder?: boolean;
    className?: string;
    isActive?: boolean;
    showCameraSelector?: boolean;
    showFlashlight?: boolean;
    showGalleryImport?: boolean;
    enableHapticFeedback?: boolean;
    enableBrightnessAdjustment?: boolean;
    highContrastMode?: boolean;
}

// Extended MediaTrackConstraintSet to include torch property
interface ExtendedMediaTrackConstraintSet extends MediaTrackConstraintSet {
    torch?: boolean;
}

// Local storage key for last selected camera
const LAST_CAMERA_KEY = 'qr-scanner-last-camera';

/**
 * Enhanced QR Scanner component using html5-qrcode library
 */
const QrScannerEnhanced: React.FC<QrScannerEnhancedProps> = ({
    onScanSuccess,
    onScanError,
    width = 300,
    height = 300,
    fps = 10,
    showBorder = true,
    className = '',
    isActive = true,
    showCameraSelector = true,
    showFlashlight = true,
    showGalleryImport = true,
    enableHapticFeedback = true,
    enableBrightnessAdjustment = true,
    highContrastMode = false,
}) => {
    // Scanner state
    const [isScanning, setIsScanning] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [cameras, setCameras] = useState<CameraDevice[]>([]);
    const [selectedCamera, setSelectedCamera] = useState<string | null>(null);
    const [isFlashlightOn, setIsFlashlightOn] = useState<boolean>(false);
    const [hasFlashlight, setHasFlashlight] = useState<boolean>(false);
    const [showCameraModal, setShowCameraModal] = useState<boolean>(false);
    const [showManualInput, setShowManualInput] = useState<boolean>(false);
    const [manualInputValue, setManualInputValue] = useState<string>('');
    const [qrDetected, setQrDetected] = useState<boolean>(false);
    const [restartScanner, setRestartScanner] = useState<boolean>(false);
    const [failedCameras, setFailedCameras] = useState<string[]>([]);
    const [usedFallbackCamera, setUsedFallbackCamera] = useState<boolean>(false);

    // Refs
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const scannerContainerId = 'qr-scanner-enhanced-container';
    const fileInputRef = useRef<HTMLInputElement>(null);
    const cameraModalRef = useRef<HTMLDivElement>(null);

    /**
     * Save the selected camera ID to localStorage
     */
    const saveLastCamera = (cameraId: string) => {
        try {
            localStorage.setItem(LAST_CAMERA_KEY, cameraId);
            console.log('Saved last camera:', cameraId);
        } catch (err) {
            console.error('Error saving camera preference:', err);
        }
    };

    /**
     * Get the last selected camera ID from localStorage
     */
    const getLastCamera = (): string | null => {
        try {
            return localStorage.getItem(LAST_CAMERA_KEY);
        } catch (err) {
            console.error('Error getting camera preference:', err);
            return null;
        }
    };

    // Load available cameras
    const loadCameras = async () => {
        try {
            const devices = await Html5Qrcode.getCameras();
            console.log('devices', devices);
            if (devices && devices.length > 0) {
                setCameras(devices);

                // Get last selected camera from localStorage
                const lastCameraId = getLastCamera();

                // Check if the last camera is available
                if (lastCameraId && devices.some((camera) => camera.id === lastCameraId)) {
                    console.log('Using last selected camera:', lastCameraId);
                    setSelectedCamera(lastCameraId);
                } else {
                    // Improved camera selection logic:
                    // Try to identify back camera by label (more reliable)
                    const backCamera = devices.find(
                        (device) =>
                            device.label.toLowerCase().includes('back') ||
                            device.label.toLowerCase().includes('rear') ||
                            device.label.toLowerCase().includes('0'),
                    );

                    // If we found a back camera, use it
                    if (backCamera) {
                        console.log('Using identified back camera:', backCamera.id, backCamera.label);
                        setSelectedCamera(backCamera.id);
                    } else {
                        // Default to first camera as it's most likely to work
                        const defaultCamera = devices[0].id;
                        console.log('No back camera identified, using first camera:', defaultCamera);
                        setSelectedCamera(defaultCamera);
                    }
                }
            }
        } catch (err) {
            console.error('Error getting cameras:', err);
            const errorMsg = err instanceof Error ? err.message : 'Failed to get cameras';
            setError(errorMsg);
            if (onScanError) onScanError(errorMsg);
        }
    };

    // Start scanner
    const startScanner = async () => {
        if (!scannerRef.current || !selectedCamera) return;

        try {
            console.log('Starting scanner with camera:', selectedCamera);
            setIsScanning(true);
            setError(null);
            setUsedFallbackCamera(false); // Reset fallback indicator

            // Calculate qrbox size based on container dimensions
            const qrboxSize = Math.min(typeof width === 'number' ? width - 50 : 250, typeof height === 'number' ? height - 50 : 250, 250);

            // Start scanning with selected camera
            try {
                await scannerRef.current.start(
                    selectedCamera,
                    {
                        fps,
                        qrbox: { width: qrboxSize, height: qrboxSize },
                        aspectRatio: 1.0,
                    },
                    (decodedText) => {
                        // Handle successful scan
                        setQrDetected(true);

                        // Provide haptic feedback
                        if (enableHapticFeedback && 'vibrate' in navigator) {
                            navigator.vibrate(100);
                        }

                        // Call success callback
                        onScanSuccess(decodedText);

                        // Reset QR detected state after animation
                        setTimeout(() => setQrDetected(false), 1000);
                    },
                    (errorMessage) => {
                        // Only handle fatal errors
                        if (errorMessage.includes('Camera access denied') || errorMessage.includes('No camera')) {
                            setError(errorMessage);
                            setIsScanning(false);
                            if (onScanError) onScanError(errorMessage);
                        }
                    },
                );
            } catch (cameraError) {
                console.error('Failed to start with selected camera, trying fallback:', cameraError);

                // Add the failed camera to our list
                setFailedCameras((prev) => [...prev.filter((id) => id !== selectedCamera), selectedCamera]);

                // If we fail with the selected camera, try to use the first camera as fallback
                if (cameras.length > 0 && cameras[0].id !== selectedCamera) {
                    console.log('Trying fallback to first camera:', cameras[0].id);

                    // Set flag that we're using a fallback camera
                    setUsedFallbackCamera(true);

                    await scannerRef.current.start(
                        cameras[0].id,
                        {
                            fps,
                            qrbox: { width: qrboxSize, height: qrboxSize },
                            aspectRatio: 1.0,
                        },
                        (decodedText) => {
                            // Handle successful scan with fallback camera
                            setQrDetected(true);
                            if (enableHapticFeedback && 'vibrate' in navigator) {
                                navigator.vibrate(100);
                            }
                            onScanSuccess(decodedText);
                            setTimeout(() => setQrDetected(false), 1000);

                            // Update selected camera to the working one
                            setSelectedCamera(cameras[0].id);
                            saveLastCamera(cameras[0].id);
                        },
                        (errorMessage) => {
                            if (errorMessage.includes('Camera access denied') || errorMessage.includes('No camera')) {
                                setError(errorMessage);
                                setIsScanning(false);
                                if (onScanError) onScanError(errorMessage);
                            }
                        },
                    );
                } else {
                    // If fallback fails or no other camera available, show error
                    throw cameraError;
                }
            }

            // Check for flashlight capability
            const videoElement = document.getElementById(scannerContainerId)?.querySelector('video');
            if (videoElement) {
                const stream = (videoElement as HTMLVideoElement).srcObject as MediaStream;
                if (stream) {
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack && 'getCapabilities' in videoTrack) {
                        const capabilities = videoTrack.getCapabilities();
                        setHasFlashlight('torch' in capabilities);
                    }
                }
            }
        } catch (err) {
            const errorMsg = err instanceof Error ? err.message : 'Failed to start scanner';
            console.error('Error starting scanner:', errorMsg);
            setError(errorMsg);
            setIsScanning(false);
            if (onScanError) onScanError(errorMsg);
        }
    };

    // Stop scanner
    const stopScanner = async () => {
        if (!scannerRef.current || !isScanning) return;

        try {
            console.log('Stopping scanner');
            // Turn off flashlight if it's on
            if (isFlashlightOn) {
                const videoElement = document.getElementById(scannerContainerId)?.querySelector('video');
                if (videoElement) {
                    const stream = (videoElement as HTMLVideoElement).srcObject as MediaStream;
                    if (stream) {
                        const videoTrack = stream.getVideoTracks()[0];
                        if (videoTrack && 'applyConstraints' in videoTrack) {
                            // Use type assertion to bypass TypeScript check
                            const constraints: MediaTrackConstraints = {
                                advanced: [{ torch: false } as ExtendedMediaTrackConstraintSet],
                            };
                            await videoTrack.applyConstraints(constraints);
                        }
                    }
                }
                setIsFlashlightOn(false);
            }

            // Stop the scanner
            await scannerRef.current.stop();
            setIsScanning(false);
        } catch (err) {
            console.error('Error stopping scanner:', err);
        }
    };

    // Initialize scanner on mount
    useEffect(() => {
        console.log('Initializing scanner');
        scannerRef.current = new Html5Qrcode(scannerContainerId);
        loadCameras();

        // Clean up on unmount
        return () => {
            if (scannerRef.current && scannerRef.current.getState() === Html5QrcodeScannerState.SCANNING) {
                scannerRef.current.stop().catch((err) => {
                    console.error('Error stopping scanner on unmount:', err);
                });
            }
        };
    }, []);

    // Handle changes to isActive prop or selectedCamera
    useEffect(() => {
        const handleScannerState = async () => {
            console.log('Scanner state changed:', { isActive, selectedCamera, isScanning, restartScanner });

            // If scanner should be active and we have a camera selected
            if (isActive && selectedCamera) {
                // If scanner is not running or needs to be restarted
                if (!isScanning || restartScanner) {
                    // If scanner is running, stop it first
                    if (isScanning) {
                        await stopScanner();
                    }

                    // Reset restart flag
                    if (restartScanner) {
                        setRestartScanner(false);
                    }

                    // Start scanner with selected camera
                    await startScanner();
                }
            }
            // If scanner should not be active but is running
            else if (!isActive && isScanning) {
                await stopScanner();
            }
        };

        handleScannerState();
    }, [isActive, selectedCamera, restartScanner]);

    // Handle camera change
    const handleCameraChange = async (cameraId: string) => {
        console.log('Changing camera to:', cameraId);
        setSelectedCamera(cameraId);
        setShowCameraModal(false);

        // Clear this camera from failed list if it's there
        // (user is explicitly choosing it, so give it another chance)
        if (failedCameras.includes(cameraId)) {
            setFailedCameras((prev) => prev.filter((id) => id !== cameraId));
        }

        // Save the selected camera to localStorage
        saveLastCamera(cameraId);

        // Set restart flag to trigger scanner restart in useEffect
        setRestartScanner(true);
    };

    // Toggle flashlight
    const toggleFlashlight = async () => {
        if (!hasFlashlight || !isScanning) return;

        try {
            const videoElement = document.getElementById(scannerContainerId)?.querySelector('video');
            if (videoElement) {
                const stream = (videoElement as HTMLVideoElement).srcObject as MediaStream;
                if (stream) {
                    const videoTrack = stream.getVideoTracks()[0];
                    if (videoTrack && 'applyConstraints' in videoTrack) {
                        // Use type assertion to bypass TypeScript check
                        const constraints: MediaTrackConstraints = {
                            advanced: [{ torch: !isFlashlightOn } as ExtendedMediaTrackConstraintSet],
                        };
                        await videoTrack.applyConstraints(constraints);
                        setIsFlashlightOn(!isFlashlightOn);
                    }
                }
            }
        } catch (err) {
            console.error('Error toggling flashlight:', err);
        }
    };

    // Handle gallery import
    const handleGalleryImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !scannerRef.current) return;

        try {
            // Stop scanning while processing image
            if (isScanning) {
                await stopScanner();
            }

            // Scan the file
            const result = await scannerRef.current.scanFile(file, false);

            // Handle successful scan
            setQrDetected(true);
            if (enableHapticFeedback && 'vibrate' in navigator) {
                navigator.vibrate(100);
            }
            onScanSuccess(result);
            setTimeout(() => setQrDetected(false), 1000);
        } catch (err) {
            const errorMsg = 'Failed to scan QR code from image';
            setError(errorMsg);
            if (onScanError) onScanError(errorMsg);
        } finally {
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }

            // Restart scanner if it was active
            if (isActive && selectedCamera) {
                setRestartScanner(true);
            }
        }
    };

    // Handle manual input
    const handleManualInputSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (manualInputValue.trim()) {
            // Handle successful manual input
            setQrDetected(true);
            if (enableHapticFeedback && 'vibrate' in navigator) {
                navigator.vibrate(100);
            }
            onScanSuccess(manualInputValue.trim());
            setManualInputValue('');
            setShowManualInput(false);
            setTimeout(() => setQrDetected(false), 1000);
        }
    };

    // Convert width and height to style values
    const widthStyle = typeof width === 'number' ? `${width}px` : width;
    const heightStyle = typeof height === 'number' ? `${height}px` : height;

    // Determine border color based on high contrast mode
    const borderColor = highContrastMode ? 'border-yellow-400' : 'border-blue-500';
    const scanlineColor = highContrastMode ? 'bg-yellow-400' : 'bg-blue-500';

    return (
        <div className={`qr-scanner-enhanced ${className} ${highContrastMode ? 'high-contrast-mode' : ''}`}>
            <div className={`scanner-card rounded-lg ${highContrastMode ? 'bg-black' : 'bg-white'} p-4 shadow-lg`}>
                {/* Scanner viewport with visual guides */}
                <div className="scanner-viewport-container relative mb-4">
                    {/* Actual scanner container */}
                    <div
                        id={scannerContainerId}
                        className="scanner-viewport overflow-hidden rounded-lg"
                        style={{
                            width: widthStyle,
                            height: heightStyle,
                            position: 'relative',
                        }}
                    ></div>

                    {/* Overlay with scanning guides */}
                    {isScanning && !error && !showManualInput && (
                        <div className="pointer-events-none absolute inset-0 overflow-hidden">
                            {/* Corner indicators */}
                            <div className={`absolute top-0 left-0 h-20 w-20 border-t-3 border-l-3 ${borderColor}`}></div>
                            <div className={`absolute top-0 right-0 h-20 w-20 border-t-3 border-r-3 ${borderColor}`}></div>
                            <div className={`absolute bottom-0 left-0 h-20 w-20 border-b-3 border-l-3 ${borderColor}`}></div>
                            <div className={`absolute right-0 bottom-0 h-20 w-20 border-r-3 border-b-3 ${borderColor}`}></div>

                            {/* Target area indicator */}
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className={`h-48 w-48 rounded-lg border-2 border-dashed ${borderColor} opacity-70`}></div>
                            </div>

                            {/* Scanning animation */}
                            <div className={`animate-scanline absolute top-1/2 left-0 h-0.5 w-full ${scanlineColor} opacity-70`}></div>
                        </div>
                    )}

                    {/* Manual input overlay */}
                    {showManualInput && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/90 p-4 dark:bg-black/90">
                            <form onSubmit={handleManualInputSubmit} className="w-full max-w-sm">
                                <h3 className={`mb-2 text-lg font-medium ${highContrastMode ? 'text-yellow-400' : 'text-gray-800 dark:text-white'}`}>
                                    Enter QR Code Manually
                                </h3>
                                <div className="flex">
                                    <input
                                        type="text"
                                        value={manualInputValue}
                                        onChange={(e) => setManualInputValue(e.target.value)}
                                        className="flex-1 rounded-l-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                                        placeholder="Enter URL or text from QR code"
                                    />
                                    <button
                                        type="submit"
                                        className="rounded-r-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
                                    >
                                        Submit
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setShowManualInput(false)}
                                    className="mt-2 text-sm text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                >
                                    Cancel
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                {/* Controls */}
                <div className="scanner-controls mb-4 flex items-center justify-center space-x-4">
                    {/* Flashlight toggle */}
                    {showFlashlight && hasFlashlight && (
                        <button
                            className={`flashlight-btn flex h-10 w-10 items-center justify-center rounded-full ${
                                isFlashlightOn ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                            onClick={toggleFlashlight}
                            aria-label={isFlashlightOn ? 'Turn off flashlight' : 'Turn on flashlight'}
                        >
                            {isFlashlightOn ? <Zap size={20} /> : <ZapOff size={20} />}
                        </button>
                    )}

                    {/* Camera selector */}
                    {showCameraSelector && cameras.length > 0 && (
                        <>
                            <button
                                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                onClick={() => setShowCameraModal(true)}
                                aria-label="Select camera"
                                aria-expanded={showCameraModal}
                                aria-haspopup="dialog"
                            >
                                <Camera size={20} />
                            </button>

                            {/* Camera selection modal */}
                            <AnimatePresence>
                                {showCameraModal && (
                                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
                                        <motion.div
                                            className="camera-selector-modal w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg dark:bg-gray-800"
                                            initial={{ opacity: 0, scale: 0.9 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0, scale: 0.9 }}
                                            transition={{ duration: 0.2 }}
                                        >
                                            <div className="border-b border-gray-200 p-4 dark:border-gray-700">
                                                <div className="flex items-center justify-between">
                                                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">Select Camera</h3>
                                                    <button
                                                        onClick={() => setShowCameraModal(false)}
                                                        className="rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-500 dark:hover:bg-gray-700"
                                                        aria-label="Close"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-6 w-6"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M6 18L18 6M6 6l12 12"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="max-h-60 overflow-y-auto p-2">
                                                {cameras.map((camera) => (
                                                    <button
                                                        key={camera.id}
                                                        className={`camera-option mb-2 w-full rounded-lg px-4 py-3 text-left text-sm ${
                                                            selectedCamera === camera.id
                                                                ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                                                : 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                                        }`}
                                                        onClick={() => {
                                                            handleCameraChange(camera.id);
                                                            setShowCameraModal(false);
                                                        }}
                                                    >
                                                        <div className="flex items-center justify-between">
                                                            <span>{camera.label || `Camera ${cameras.indexOf(camera) + 1}`}</span>
                                                            {failedCameras.includes(camera.id) && (
                                                                <span className="ml-2 inline-flex rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
                                                                    Issue detected
                                                                </span>
                                                            )}
                                                            {selectedCamera === camera.id && (
                                                                <span className="ml-2 inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                                                                    Selected
                                                                </span>
                                                            )}
                                                        </div>
                                                    </button>
                                                ))}
                                            </div>
                                            <div className="border-t border-gray-200 p-4 dark:border-gray-700">
                                                <button
                                                    className="w-full rounded-lg bg-blue-500 py-2 text-center text-white hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:outline-none"
                                                    onClick={() => setShowCameraModal(false)}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        </motion.div>
                                    </div>
                                )}
                            </AnimatePresence>
                        </>
                    )}

                    {/* Gallery import */}
                    {showGalleryImport && (
                        <div className="gallery-import">
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleGalleryImport}
                                aria-label="Import from gallery"
                            />
                            <button
                                className="gallery-import-btn flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                                onClick={() => fileInputRef.current?.click()}
                                aria-label="Import from gallery"
                            >
                                <Image size={20} />
                            </button>
                        </div>
                    )}

                    {/* Manual input button */}
                    {showManualInput && (
                        <button
                            className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                            onClick={() => setShowManualInput(true)}
                            aria-label="Enter code manually"
                        >
                            <Keyboard size={20} />
                        </button>
                    )}
                </div>

                {/* Status indicator */}
                <div className="scanner-status">
                    <div className="flex flex-col items-center justify-center">
                        <div className="flex items-center">
                            <div className="mr-2 h-3 w-3 animate-pulse rounded-full bg-green-500"></div>
                            <span className="scanner-text text-sm text-gray-600 dark:text-gray-300">
                                {error ? `Error: ${error}` : isScanning ? 'Position QR code within the frame' : 'Starting camera...'}
                            </span>
                        </div>

                        {/* Fallback camera notification */}
                        {usedFallbackCamera && (
                            <div className="mt-2 flex items-center justify-center">
                                <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                                    Original camera failed, using fallback camera
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Voice guidance for screen readers */}
            <div className="sr-only" aria-live="polite">
                Position the QR code within the square frame to scan
            </div>

            {/* Add custom styles for animations */}
            <style>
                {`
                @keyframes scanline {
                    0% {
                        transform: translateY(-100px);
                        opacity: 0;
                    }
                    50% {
                        opacity: 1;
                    }
                    100% {
                        transform: translateY(100px);
                        opacity: 0;
                    }
                }
                .animate-scanline {
                    animation: scanline 2s linear infinite;
                }
                `}
            </style>
        </div>
    );
};

export default QrScannerEnhanced;
